/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authService, User, demoLogin, demoKeyLogin } from '../services/authService';
import { apiService } from '../services/apiService';

export interface AuthContextType {
  user: User | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  session: any;
  isLoading: boolean;
  isDemo: boolean;
  showOnboardingSurvey: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string, fullName?: string) => Promise<any>;
  signOut: () => Promise<void>;
  signInDemo: () => Promise<any>;
  signInDemoKey: (demoKey: string) => Promise<any>;
  getApiHeaders: () => Promise<Record<string, string>>;
  refreshAuth: () => Promise<void>;
  dismissOnboardingSurvey: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const [showOnboardingSurvey, setShowOnboardingSurvey] = useState(false);

  // Define signOut early so it can be used in useEffects below
  const signOut = useCallback(async () => {
    if (isDemo) {
      // Clear demo session
      localStorage.removeItem('demo_session');
      setIsDemo(false);
    } else {
      await authService.signOut();
    }
    setUser(null);
    setSession(null);
    // Clear cached user data on sign out
    localStorage.removeItem('cached_user');
  }, [isDemo]);

  useEffect(() => {
    // Clear any potentially corrupted auth data on app startup
    const clearCorruptedData = () => {
      try {
        const cachedUser = localStorage.getItem('cached_user');
        if (cachedUser) {
          JSON.parse(cachedUser); // Test if it's valid JSON
        }
      } catch (_e) {
        console.log('🧹 Clearing corrupted cached data on startup');
        localStorage.removeItem('cached_user');
      }
    };

    clearCorruptedData();

    // Check if there's a demo session stored
    const demoSession = localStorage.getItem('demo_session');
    if (demoSession) {
      try {
        const parsed = JSON.parse(demoSession);
        if (parsed.session && parsed.user) {
          console.log('🎭 Restoring demo session');
          setSession(parsed.session);
          setUser(parsed.user);
          setIsDemo(true);
          setIsLoading(false);
          return;
        }
      } catch (_e) {
        console.log('🧹 Clearing invalid demo session');
        localStorage.removeItem('demo_session');
      }
    }

    // Check if we're on a public route (non-app route)
    const isPublicRoute =
      !window.location.pathname.startsWith('/app') && window.location.pathname !== '/login';

    // Get initial session
    const getSession = async () => {
      console.log('🔄 Getting initial session...');
      if (!authService.isAvailable()) {
        console.error('Auth service not available');
        setIsLoading(false);
        return;
      }

      try {
        // Add timeout to prevent indefinite hanging on slow/unresponsive Supabase
        const sessionPromise = authService.getSession();
        const timeoutPromise = new Promise<null>((_, reject) =>
          setTimeout(() => reject(new Error('Session timeout')), 8000)
        );

        const session = await Promise.race([sessionPromise, timeoutPromise]);
        console.log('📱 Initial session:', !!session?.user);
        setSession(session);
        if (session?.user) {
          // On page refresh, try to use cached user data first for faster loading
          const cachedUser = localStorage.getItem('cached_user');
          if (cachedUser) {
            try {
              const parsedUser = JSON.parse(cachedUser);
              console.log('💾 Using cached user data');
              setUser(parsedUser);

              // For public routes, immediately mark as loaded so page renders
              if (isPublicRoute) {
                console.log('🌐 Public route detected - quick load');
                setIsLoading(false);
              }

              // Fetch fresh user data in background
              fetchUserDetails(session.user, true);

              // If not public route and we have cached data, still set loading false after cache
              if (!isPublicRoute) {
                setIsLoading(false);
              }
              return;
            } catch (_e) {
              console.log('❌ Invalid cached user data, removing');
              localStorage.removeItem('cached_user');
            }
          }

          // If on public route, don't wait for user details - load page immediately
          if (isPublicRoute) {
            console.log('🌐 Public route - loading in background');
            setIsLoading(false);
            fetchUserDetails(session.user, true);
          } else {
            await fetchUserDetails(session.user);
          }
        } else {
          console.log('👤 No user in session, setting loading to false');
          // Clear any cached user data
          localStorage.removeItem('cached_user');
        }
      } catch (error) {
        console.error('Failed to get session:', error);
        // Clear potentially corrupted cached data
        localStorage.removeItem('cached_user');
      } finally {
        console.log('✅ Setting loading to false');
        setIsLoading(false);
      }
    };

    getSession();

    // Listen for auth state changes
    if (authService.isAvailable()) {
      const subscription = authService.onAuthStateChange(async (event, session) => {
        console.log('Auth state changed:', event);
        setSession(session);

        try {
          if (session?.user) {
            await fetchUserDetails(session.user);
          } else {
            setUser(null);
            // Clear cached data when session is lost
            localStorage.removeItem('cached_user');
          }
        } catch (error) {
          console.error('Error in auth state change handler:', error);
          setUser(null);
        } finally {
          setIsLoading(false);
        }
      });

      // Add visibility change listener to refresh token when tab becomes active
      const handleVisibilityChange = async () => {
        if (document.visibilityState === 'visible') {
          console.log('👀 Tab became visible, checking session...');

          // Only check if we have a session
          const currentSession = await authService.getSession();
          if (currentSession && authService.isTokenExpired(currentSession)) {
            console.log('⏰ Token expired while tab was hidden, refreshing...');
            try {
              const validSession = await authService.getValidSession();
              if (validSession) {
                setSession(validSession);
              } else {
                console.log('⚠️ Could not refresh session, signing out');
                await signOut();
              }
            } catch (error) {
              console.error('❌ Failed to refresh on visibility change:', error);
              await signOut();
            }
          }
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Listen for unauthorized events from API calls
      const handleUnauthorized = async () => {
        console.log('🚨 Unauthorized event received, attempting to refresh auth...');
        try {
          const validSession = await authService.getValidSession();
          if (validSession) {
            console.log('✅ Session refreshed after 401 error');
            setSession(validSession);
          } else {
            console.log('⚠️ Could not refresh session after 401, signing out');
            await signOut();
          }
        } catch (error) {
          console.error('❌ Failed to refresh after 401:', error);
          await signOut();
        }
      };

      window.addEventListener('auth:unauthorized', handleUnauthorized);

      return () => {
        subscription.unsubscribe();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('auth:unauthorized', handleUnauthorized);
      };
    }
  }, [signOut]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchUserDetails = async (authUser: any, isBackgroundUpdate = false) => {
    console.log(
      '👤 Fetching user details for:',
      authUser?.email,
      isBackgroundUpdate ? '(background)' : ''
    );
    if (!authService.isAvailable()) {
      console.error('Auth service not available');
      const fallbackUser = authService.createFallbackUser(authUser);
      setUser(fallbackUser);
      // Cache fallback user for faster loading
      localStorage.setItem('cached_user', JSON.stringify(fallbackUser));
      return;
    }

    try {
      // Get current session to ensure we have the latest token
      console.log('🔄 Getting current session...');
      // Add timeout to prevent hanging
      const sessionPromise = authService.getSession();
      const sessionTimeoutPromise = new Promise<null>((_, reject) =>
        setTimeout(() => reject(new Error('Session timeout')), 5000)
      );
      const currentSession = await Promise.race([sessionPromise, sessionTimeoutPromise]);
      const authHeaders = authService.getApiHeaders(currentSession);

      console.log('🔑 Fetching user details with auth');

      // Add shorter timeout for background updates
      const timeout = isBackgroundUpdate ? 5000 : 10000;
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('API timeout')), timeout)
      );

      const userData = await Promise.race([apiService.getCurrentUser(authHeaders), timeoutPromise]);
      console.log('✅ User data received:', (userData as any)?.email);
      setUser(userData as User);

      // Cache user data for future page loads
      localStorage.setItem('cached_user', JSON.stringify(userData));
    } catch (error) {
      console.error('Error fetching user details:', error);

      // For background updates, don't change user state if it fails
      if (isBackgroundUpdate) {
        console.log('🔄 Background update failed, keeping current user');
        return;
      }

      // Fallback: create user from auth data
      console.log('🔄 Creating fallback user');
      const fallbackUser = authService.createFallbackUser(authUser);
      setUser(fallbackUser);
      // Cache fallback user for faster loading
      localStorage.setItem('cached_user', JSON.stringify(fallbackUser));
    }
  };

  const signIn = async (email: string, password: string) => {
    const data = await authService.signIn({ email, password });
    return data;
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const data = await authService.signUp({ email, password, fullName });
    return data;
  };

  const signInDemo = async () => {
    console.log('🎭 Starting demo login...');
    try {
      const data = await demoLogin();
      setUser(data.user);
      setSession(data.session);
      setIsDemo(true);
      // Store demo session for persistence
      localStorage.setItem(
        'demo_session',
        JSON.stringify({
          user: data.user,
          session: data.session,
        })
      );
      localStorage.setItem('cached_user', JSON.stringify(data.user));
      console.log('✅ Demo session established');
      return data;
    } catch (error) {
      console.error('❌ Demo login failed:', error);
      throw error;
    }
  };

  const signInDemoKey = async (demoKey: string) => {
    console.log('🔑 Starting demo key login...');
    try {
      const data = await demoKeyLogin(demoKey);
      setUser(data.user);
      setSession(data.session);
      setIsDemo(true);
      // Store demo session for persistence
      localStorage.setItem(
        'demo_session',
        JSON.stringify({
          user: data.user,
          session: data.session,
        })
      );
      localStorage.setItem('cached_user', JSON.stringify(data.user));
      console.log('✅ Demo key session established');
      return data;
    } catch (error) {
      console.error('❌ Demo key login failed:', error);
      throw error;
    }
  };

  const refreshAuth = async () => {
    console.log('🔄 Manual auth refresh requested...');
    if (!authService.isAvailable()) {
      console.error('Auth service not available');
      return;
    }

    try {
      const validSession = await authService.getValidSession();
      if (validSession?.user) {
        console.log('✅ Auth refreshed successfully');
        setSession(validSession);
        await fetchUserDetails(validSession.user, true);
      } else {
        console.log('⚠️ No valid session after refresh, signing out');
        await signOut();
      }
    } catch (error) {
      console.error('❌ Failed to refresh auth:', error);
      // On auth refresh failure, sign out the user
      await signOut();
    }
  };

  const getApiHeaders = useCallback(async () => {
    // Get a valid session (will refresh if expired)
    const validSession = await authService.getValidSession();

    if (validSession) {
      // Only update session state if the access token actually changed
      // Using reference equality (validSession !== session) causes infinite re-renders
      // because getValidSession() returns a new object each time
      if (validSession.access_token !== session?.access_token) {
        console.log('🔄 Session token changed, updating state');
        setSession(validSession);
      }
      return authService.getApiHeaders(validSession);
    }

    console.log('⚠️ No valid session available for API headers');
    return authService.getApiHeaders(session);
  }, [session]);

  // Check if user needs to complete onboarding survey
  // TEMPORARILY DISABLED: Still causing issues even with safeguards
  // TODO: Re-enable after rate limiting is fully resolved
  useEffect(() => {
    // Disabled to prevent rate limiting and loops
    return;

    /* Implementation with safeguards - temporarily disabled
    // Skip if no user, demo mode, or already checked this session
    if (!user || !session || isDemo) return;

    // Check if we've already checked the survey in this session
    const sessionKey = `survey_checked_${user.id}`;
    const alreadyChecked = sessionStorage.getItem(sessionKey);

    if (alreadyChecked) {
      console.log('✓ Survey already checked this session, skipping');
      return;
    }

    // Check if user dismissed it this session
    const dismissed = sessionStorage.getItem('survey_dismissed');
    if (dismissed) {
      console.log('✓ Survey dismissed this session, skipping');
      return;
    }

    // Mark as checked BEFORE making the API call to prevent duplicate requests
    sessionStorage.setItem(sessionKey, 'true');

    // Delay to let the app load first
    const timer = setTimeout(async () => {
      try {
        console.log('🔍 Checking survey status...');
        const headers = await getApiHeaders();
        const survey = await apiService.getUserSurvey(headers);

        // Show survey if user hasn't completed or skipped it
        if (!survey || (survey.status !== 'completed' && survey.status !== 'skipped')) {
          console.log('📋 Survey pending, showing modal');
          setShowOnboardingSurvey(true);
        } else {
          console.log('✓ Survey already completed/skipped');
        }
      } catch (_err) {
        // If survey API fails, don't block the user
        console.log('⚠️ Survey check failed, continuing without survey:', err);
      }
    }, 2000); // Increased delay to 2 seconds to ensure app is fully loaded

    return () => clearTimeout(timer);
    */

    // Critical: Only depend on user.id, session existence, and isDemo
    // Do NOT include getApiHeaders to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, !!session, isDemo]);

  const dismissOnboardingSurvey = () => {
    setShowOnboardingSurvey(false);
    sessionStorage.setItem('survey_dismissed', 'true');
  };

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isDemo,
    showOnboardingSurvey,
    signIn,
    signUp,
    signOut,
    signInDemo,
    signInDemoKey,
    getApiHeaders,
    refreshAuth,
    dismissOnboardingSurvey,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
